"use client";

import { useEffect, useRef, useState } from "react";
import type { BluetoothOptions } from "@/lib/bluetooth";
import {
  autoConnectSensor,
  connectSensor,
  disconnectSensor,
  getSensorState,
  subscribeSensorLines,
  subscribeSensorState,
  updateSensorOptions,
} from "@/lib/sensorStreamHub";

export type SensorSample = {
  raw: string;
  gsrRawAdc?: number;
  gsrSmoothAdc?: number;
  gsrUiLevel?: number;
  timestamp: number;
};

type ParseFn = (line: string) => Partial<SensorSample> | void;

export type UseSensorStreamOptions = {
  bluetooth?: BluetoothOptions;
  autoConnect?: boolean;
  trackLastSample?: boolean;
};

export function useSensorStream(
  parseLine?: ParseFn,
  options: UseSensorStreamOptions = {}
) {
  const shouldTrackLastSample = options.trackLastSample ?? true;
  const [sensorState, setSensorState] = useState(() => getSensorState());
  const [trackedLastSample, setTrackedLastSample] = useState<SensorSample | null>(
    null
  );
  const handlerRef = useRef<ParseFn | undefined>(parseLine);
  const sensorStateRef = useRef(sensorState);
  const trackLastSampleRef = useRef(shouldTrackLastSample);

  useEffect(() => {
    handlerRef.current = parseLine;
  }, [parseLine]);

  useEffect(() => {
    trackLastSampleRef.current = shouldTrackLastSample;
  }, [shouldTrackLastSample]);

  useEffect(() => {
    updateSensorOptions({
      bluetooth: options.bluetooth,
    });
  }, [options.bluetooth]);

  useEffect(() => {
    sensorStateRef.current = sensorState;
  }, [sensorState]);

  useEffect(() => {
    const handleState = (nextState: ReturnType<typeof getSensorState>) => {
      const previousState = sensorStateRef.current;
      sensorStateRef.current = nextState;

      if (nextState.isConnected !== previousState.isConnected) {
        if (trackLastSampleRef.current) {
          setTrackedLastSample(null);
        }
      }

      setSensorState(nextState);
    };

    return subscribeSensorState(handleState);
  }, []);

  useEffect(() => {
    const handler = (line: string) => {
      const parsed = handlerRef.current ? handlerRef.current(line) ?? {} : {};
      if (trackLastSampleRef.current) {
        setTrackedLastSample({
          raw: line,
          timestamp: Date.now(),
          ...parsed,
        });
      }
    };

    return subscribeSensorLines(handler);
  }, []);

  useEffect(() => {
    if (options.autoConnect === false) return;
    void autoConnectSensor();
  }, [options.autoConnect]);

  const lastSample = shouldTrackLastSample ? trackedLastSample : null;

  return {
    isSupported: sensorState.isSupported,
    isConnecting: sensorState.isConnecting,
    supportMessage: sensorState.supportMessage,
    isConnected: sensorState.isConnected,
    connectedDeviceName: sensorState.connectedDeviceName,
    connect: connectSensor,
    disconnect: disconnectSensor,
    lastSample,
    error: sensorState.error,
  };
}
